import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ShieldCheck, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PHONE, PHONE_HREF, EMAIL, LICENSE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_TYPES = ["Residential Remodel", "Custom Home Build", "Addition", "Roofing", "Design & Engineering", "Commercial Contracting", "Commercial New Design & Build", "Commercial Remodeling", "General Enquiry"];

const inputCls =
  "w-full bg-[#0C0C0F] border border-[#27272A] px-4 py-3 text-sm text-[#F4F2EE] placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "Residential Remodel", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received - we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", project_type: "Residential Remodel", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at " + PHONE + ".");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact McMullen Construction Co. - Call 321-313-0996"
        description="Start your remodel or new construction project. Call 321-313-0996 or send an enquiry - McMullen Construction Co., licensed Florida general contractor, responds personally."
      />
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        lines={["Tell us about", "your project."]}
        intro="Every build starts with a conversation. Send an enquiry and we'll respond personally - or call us directly."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="contact-section">
        <div className="lg:col-span-5 space-y-5">
          <Reveal>
            <a
              href={PHONE_HREF}
              data-testid="contact-phone-card"
              className="block bg-[#121216]/90 border border-[rgba(212,175,55,0.25)] p-7 hover:border-[rgba(243,224,129,0.55)] transition-colors duration-300"
            >
              <Phone className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
              <p className="mt-4 eyebrow">Call Us</p>
              <p className="mt-2 font-display text-2xl text-[#F4F2EE]">{PHONE}</p>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="block bg-[#121216]/90 border border-[rgba(212,175,55,0.25)] p-7 hover:border-[rgba(243,224,129,0.55)] transition-colors duration-300"
            >
              <Mail className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
              <p className="mt-4 eyebrow">Email</p>
              <p className="mt-2 text-sm text-[#F4F2EE] break-all">{EMAIL}</p>
            </a>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="bg-[#121216]/90 border border-[rgba(212,175,55,0.25)] p-7" data-testid="contact-license-card">
              <ShieldCheck className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
              <p className="mt-4 eyebrow">Credentials</p>
              <p className="mt-2 text-sm text-[#F4F2EE]">Licensed Florida General Contractor</p>
              <p className="font-mono-accent text-xs tracking-[0.15em] text-[#D4AF37] mt-1">{LICENSE}</p>
              <p className="mt-3 text-sm text-[#A1A1AA]">Serving homeowners across Florida.</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="bg-[#121216]/90 border border-[rgba(212,175,55,0.25)] p-7 sm:p-10" data-testid="contact-form">
            <p className="eyebrow mb-8">Request a Consultation</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Phone</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(321) 555-0100" />
              </div>
              <div>
                <label htmlFor="contact-project-type" className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Project Type</label>
                <select id="contact-project-type" data-testid="contact-project-type-select" value={form.project_type} onChange={update("project_type")} className={inputCls}>
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Project Details *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={update("message")} className={inputCls} placeholder="Tell us about your remodel, addition, new build or roofing project..." />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold text-black hover:bg-[#F3E081] transition-colors duration-300 disabled:opacity-60"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
